import * as React from 'react';

import {ListGroupProps, ListGroup, cleanChildren} from './ListGroup';
import {ListGroupItem} from './ListGroupItem';

export type SearchKeywordsCacheContext = Map<object, string[]> | undefined;

export const SearchKeywordsCache = React.createContext<SearchKeywordsCacheContext>(new Map());

const delimiter: RegExp = /[^a-zA-Z0-9]/g;

export interface SearchableListProps<T> extends ListGroupProps {
    search: string | undefined;
    searchTextsFactory: (entry: T) => string[];
    onListUpdate?: (results: T[]) => void;
}

type SearchableListType = <T>(props: SearchableListProps<T>) => React.ReactElement<SearchableListProps<T>>;

export const SearchableListGroup: SearchableListType = <T extends {}>(props: SearchableListProps<T>) => {
    const {search, searchTextsFactory, onListUpdate, ...rest} = props;
    const children = React.useMemo(() => cleanChildren(props.children), [props.children]);
    const cache = React.useContext(SearchKeywordsCache);
    const debounced = useDebounce(search, 100);
    const [filteredChildren, setFilteredChildren] = React.useState<any[]>(children);

    React.useMemo(() => {
        const searchTerms: string[] = search
            ? search
                .trim()
                .split(delimiter)
                .map((term) => term.toLowerCase())
                .filter((term) => term.length > 0)
            : [];

        // Separate displayed children, list items and their data.
        const fChildren: any[] = [];
        const filteredEntries: T[] = [];
        children.forEach((child) => {
            if (child.type !== ListGroupItem) {
                fChildren.push(child);
            } else if (entrySatisfiesSearch(child!.props.item, searchTerms, searchTextsFactory, cache)) {
                filteredEntries.push(child.props.item);
                fChildren.push(child);
            }
        });
        setFilteredChildren(fChildren);
        if (onListUpdate) {
            onListUpdate(filteredEntries);
        }
    }, [debounced, children]);

    return <ListGroup {...rest}>{filteredChildren}</ListGroup>;
};

function entrySatisfiesSearch<T>(entry: T, searchTerms: string[], searchTextsFactory: (entry: T) => string[], cache: SearchKeywordsCacheContext): boolean {
    const keywords: string[] = getKeywords(entry, searchTextsFactory, cache);

    return searchTerms.every((term: string) => {
        return keywords.some((keyword: string) => {
            return keyword.includes(term);
        });
    });
}

function getKeywords<T extends {}>(entry: T, searchTextsFactory: (entry: T) => string[], cache: Map<object, string[]> | undefined): string[] {
    const searchTexts = searchTextsFactory(entry);
    let keywords: string[] | undefined = (cache && cache.get(entry)) || undefined;

    if (!keywords) {
        keywords = [];

        for (const searchText of searchTexts) {
            keywords.push(...searchText?.split(delimiter).map((term) => term.toLocaleLowerCase()));
        }

        // Remove empty and duplicate entries
        keywords = keywords.filter((term, index, array) => {
            return term.length > 0 && array.indexOf(term) === index;
        });

        if (cache) {
            cache.set(entry, keywords);
        }
    }

    return keywords;
}

export default function useDebounce(value: any, delay = 100) {
    const [debouncedValue, setDebouncedValue] = React.useState(value);

    React.useEffect(
        () => {
            // Set debouncedValue to value (passed in) after the specified delay
            const handler = setTimeout(() => {
                setDebouncedValue(value);
            }, delay);
            return () => {
                clearTimeout(handler);
            };
        },
        [value]
    );

    return debouncedValue;
}
